import { put, select } from 'redux-saga/effects'
import { getDatasetById } from 'features/maps/state/mapsSlice'
import { MapDataset, SqlDateRangeParameter, SqlParameter, SqlParameterType } from 'features/maps/types'
import { updateQuery } from '../state/builderSagas'
import { addSqlParameter } from '../state/builderSlice'
import { NamedQueryParameters, QueryFilters } from '../types'
import { formatPlaceholder, sqlParameterItems } from './queryParametersUtils'

export function* migrateDatasetQueryFiltersToSqlParameters({
  mapId,
  sourcesQueryFilters
}: {
  mapId: string
  sourcesQueryFilters: Record<string, QueryFilters>
}) {
  try {
    for (const datasetId of Object.keys(sourcesQueryFilters)) {
      const sqlParameters: SqlParameter[] = yield select((state) => state.builder.sqlParameters)
      const queryParams = sourcesQueryFilters[datasetId]

      const sqlParameterId = `${datasetId}-date-filter`

      // this query filter has been already migrated
      if (sqlParameters.find((p) => p.id === sqlParameterId)) {
        continue
      }

      let attempt = 0
      while (true) {
        const extraTagName = attempt === 0 ? '' : ` (${attempt})`
        const extraTagSqlName = attempt === 0 ? '' : `_${attempt}`

        const name = `Date filter${extraTagName}`
        const sqlNameStart = `date_from${extraTagSqlName}`
        const sqlNameEnd = `date_to${extraTagSqlName}`

        const startDate = queryParams[NamedQueryParameters.From] || ''
        const endDate = queryParams[NamedQueryParameters.To] || ''
        const sqlParameterCandidate: SqlDateRangeParameter = {
          id: sqlParameterId,
          type: SqlParameterType.DateRange,
          name,
          start: {
            sqlName: sqlNameStart,
            value: startDate
          },
          end: {
            sqlName: sqlNameEnd,
            value: endDate
          }
        }

        const hasConflicts = checkSqlParameterConflict(sqlParameterCandidate, sqlParameters)
        if (hasConflicts) {
          attempt++
          continue
        }

        yield put(addSqlParameter(sqlParameterCandidate))

        if (attempt !== 0) {
          yield renameSqlParametersInDataset({
            mapId,
            datasetId,
            parameterMap: {
              date_from: sqlNameStart,
              date_to: sqlNameEnd
            }
          })
        }
        break
      }
    }
  } catch (error) {
    console.error(`migrateDatasetQueryFiltersToSqlParameters: failed to migrate queryFilters`, error)
  }
}

function* renameSqlParametersInDataset({
  mapId,
  datasetId,
  parameterMap
}: {
  mapId: string
  datasetId: string
  parameterMap: Record<string, string>
}) {
  try {
    const mapDataset: MapDataset | undefined = yield select((state) => getDatasetById(state, datasetId))
    if (!mapDataset || !mapDataset.queryTemplate) {
      return
    }
    const originalQueryTemplate = mapDataset.queryTemplate
    let newQueryTemplate = originalQueryTemplate
    for (const [oldSqlName, newSqlName] of Object.entries(parameterMap)) {
      newQueryTemplate = newQueryTemplate.replaceAll(formatPlaceholder(oldSqlName), formatPlaceholder(newSqlName))
    }

    if (originalQueryTemplate === newQueryTemplate) {
      // nothing todo,
      return
    }

    yield updateQuery({
      mapId,
      datasetId,
      query: newQueryTemplate
    })
  } catch (error) {
    console.error(error)
    throw new Error(`renameSqlParametersQueryDataset: ${error}`)
  }
}

export function checkSqlParameterConflict(
  candidate: SqlDateRangeParameter,
  sqlParameters: SqlParameter[]
): SqlParameter | null {
  for (const existingParameter of sqlParameters) {
    if (candidate.name === existingParameter.name) {
      return existingParameter
    }
    for (const { sqlName } of sqlParameterItems(existingParameter)) {
      if (sqlName === candidate.start.sqlName || sqlName === candidate.end.sqlName) {
        return existingParameter
      }
    }
  }
  return null
}
